<template>
  <div class="p-4 bg-gray-50 dark:bg-gray-900 min-h-screen">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="flex items-center gap-4 mb-6">
        <button @click="$router.back()" class="p-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm text-gray-500 hover:text-indigo-600 transition-colors">
          <Icon icon="mdi:arrow-left" class="text-xl" />
        </button>
        <div>
          <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Retorno de Herramienta</h1>
          <p class="text-sm text-gray-500">Registro de devolución y estado de herramientas</p>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Main Form -->
        <div class="lg:col-span-2 space-y-6">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="p-6">
              <h2 class="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <Icon icon="mdi:clipboard-text-outline" class="text-indigo-500" />
                Detalles de la Devolución
              </h2>
              
              <form @submit.prevent="handleSubmit" class="space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Salida Selection -->
                  <div class="md:col-span-2">
                    <label class="block text-xs font-bold text-gray-400 uppercase mb-1 ml-1">Seleccionar Salida Pendiente *</label>
                    <div class="relative">
                      <select 
                        v-model.number="form.idSalida" 
                        @change="onSalidaChange"
                        required
                        class="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white appearance-none transition-all"
                      >
                        <option value="0" disabled>Seleccione un registro de salida</option>
                        <option v-for="s in salidasPendientes" :key="s.idSalida" :value="s.idSalida">
                          {{ s.codigoSalida }} - {{ s.nombreHerramienta }} ({{ s.cantidadSalida }} unidades)
                        </option>
                      </select>
                      <Icon icon="mdi:form-select" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                    </div>
                  </div>

                  <!-- Fecha Devolución -->
                  <div>
                    <label class="block text-xs font-bold text-gray-400 uppercase mb-1 ml-1">Fecha de Devolución *</label>
                    <div class="relative">
                      <input 
                        v-model="form.fechaDevolucion" 
                        type="datetime-local" 
                        required
                        class="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white transition-all"
                      />
                      <Icon icon="mdi:calendar-clock" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                    </div>
                  </div>

                  <!-- Cantidad -->
                  <div>
                    <label class="block text-xs font-bold text-gray-400 uppercase mb-1 ml-1">Cantidad a Devolver *</label>
                    <div class="relative">
                      <input 
                        v-model.number="form.cantidadDevuelta" 
                        type="number" 
                        :max="maxCantidad"
                        min="1"
                        required
                        class="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white transition-all"
                      />
                      <Icon icon="mdi:numeric" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                    </div>
                  </div>

                  <!-- Condición -->
                  <div>
                    <label class="block text-xs font-bold text-gray-400 uppercase mb-1 ml-1">Estado de la Herramienta *</label>
                    <div class="relative">
                      <select 
                        v-model="form.condicion" 
                        required
                        class="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white appearance-none transition-all"
                      >
                        <option value="bueno">Excelente / Bueno</option>
                        <option value="dañado">Dañado / Necesita Reparación</option>
                        <option value="faltante_piezas">Faltante de piezas</option>
                        <option value="inservible">Inservible / Chatarra</option>
                      </select>
                      <Icon icon="mdi:shield-check-outline" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                    </div>
                  </div>

                  <!-- Usuario Recibe -->
                  <div>
                    <label class="block text-xs font-bold text-gray-400 uppercase mb-1 ml-1">Recibido Por (Almacén) *</label>
                    <div class="relative">
                      <select 
                        v-model="form.idUsuarioRecibe" 
                        required
                        class="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white appearance-none transition-all"
                      >
                        <option value="" disabled>Seleccione usuario</option>
                        <option v-for="u in usuarios" :key="u.idUsuario" :value="u.idUsuario">
                          {{ u.nombreUsuario || u.usuario }}
                        </option>
                      </select>
                      <Icon icon="mdi:account-check-outline" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                    </div>
                  </div>

                  <!-- Técnico Responsable (Lectura) -->
                  <div>
                    <label class="block text-xs font-bold text-gray-400 uppercase mb-1 ml-1">Técnico que Devuelve</label>
                    <div class="relative">
                      <input 
                        :value="selectedSalida ? selectedSalida.nombreUsuarioRecibe : '---'" 
                        type="text" 
                        readonly
                        class="w-full pl-10 pr-4 py-3 bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-sm outline-none dark:text-gray-400 font-bold"
                      />
                      <Icon icon="mdi:account-wrench" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                    </div>
                  </div>

                  <!-- Observaciones -->
                  <div class="md:col-span-2">
                    <label class="block text-xs font-bold text-gray-400 uppercase mb-1 ml-1">Observaciones / Comentarios</label>
                    <textarea 
                      v-model="form.observaciones" 
                      rows="3"
                      placeholder="Indique cualquier detalle relevante sobre la entrega..."
                      class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white resize-none transition-all"
                    ></textarea>
                  </div>
                </div>

                <div class="pt-4 flex gap-3">
                  <button 
                    type="button" 
                    @click="$router.back()"
                    class="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all uppercase tracking-wider text-xs"
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit" 
                    :disabled="loadingSubmit"
                    class="flex-[2] py-3 px-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-100 dark:shadow-none transition-all uppercase tracking-wider text-xs flex items-center justify-center gap-2"
                  >
                    <Icon v-if="loadingSubmit" icon="svg-spinners:ring-resize" />
                    {{ loadingSubmit ? 'Procesando...' : 'Confirmar Devolución' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- Sidebar Info -->
        <div class="space-y-6">
          <!-- Summary Card -->
          <div class="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-2xl p-6 text-white shadow-xl">
            <h3 class="font-bold text-lg mb-4 flex items-center gap-2">
              <Icon icon="mdi:information-outline" />
              Resumen de Salida
            </h3>
            
            <div v-if="selectedSalida" class="space-y-4">
              <div class="bg-white/10 p-3 rounded-xl backdrop-blur-sm">
                <p class="text-[10px] uppercase font-bold text-indigo-200">Herramienta</p>
                <p class="font-bold truncate">{{ selectedSalida.nombreHerramienta }}</p>
              </div>
              
              <div class="grid grid-cols-2 gap-4">
                <div class="bg-white/10 p-3 rounded-xl backdrop-blur-sm">
                  <p class="text-[10px] uppercase font-bold text-indigo-200">Código</p>
                  <p class="font-bold">{{ selectedSalida.codigoSalida }}</p>
                </div>
                <div class="bg-white/10 p-3 rounded-xl backdrop-blur-sm">
                  <p class="text-[10px] uppercase font-bold text-indigo-200">Cantidad</p>
                  <p class="font-bold">{{ selectedSalida.cantidadSalida }}</p>
                </div>
              </div>

              <div class="bg-white/10 p-3 rounded-xl backdrop-blur-sm">
                <p class="text-[10px] uppercase font-bold text-indigo-200">Técnico Responsable</p>
                <p class="font-bold">{{ selectedSalida.nombreUsuarioRecibe }}</p>
              </div>

              <div class="bg-white/10 p-3 rounded-xl backdrop-blur-sm">
                <p class="text-[10px] uppercase font-bold text-indigo-200">Fecha Salida</p>
                <p class="font-bold text-xs">{{ formatDate(selectedSalida.fechaSalida) }}</p>
              </div>
            </div>

            <div v-else class="text-center py-8 opacity-60">
              <Icon icon="mdi:tools" class="text-5xl mx-auto mb-2" />
              <p class="text-xs">Seleccione una salida para ver los detalles</p>
            </div>
          </div>

          <!-- Tips Card -->
          <div class="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 class="font-bold text-gray-800 dark:text-white text-sm mb-3 flex items-center gap-2">
              <Icon icon="mdi:lightbulb-on-outline" class="text-amber-500" />
              Recomendación
            </h3>
            <ul class="text-[11px] text-gray-500 space-y-2">
              <li class="flex gap-2">
                <Icon icon="mdi:check-circle" class="text-green-500 shrink-0" />
                <span>Verifique que la herramienta esté limpia antes de recibirla.</span>
              </li>
              <li class="flex gap-2">
                <Icon icon="mdi:check-circle" class="text-green-500 shrink-0" />
                <span>Si el estado es "Dañado", especifique el tipo de daño en observaciones.</span>
              </li>
              <li class="flex gap-2">
                <Icon icon="mdi:check-circle" class="text-green-500 shrink-0" />
                <span>El stock se actualizará automáticamente tras confirmar.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { obtenerSalidasHerramienta } from "@/api/salidaHerramienta";
import { crearDevolucionHerramienta } from "@/api/devolucionHerramienta";
import { obtenerUsuarios } from "@/api/usuarios";
import { getCurrentUser } from "@/api/index";

export default {
  name: "DevolucionHerramientaCreate",
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      salidasPendientes: [],
      usuarios: [],
      loadingSubmit: false,
      selectedSalida: null,
      maxCantidad: 1,
      form: {
        idSalida: 0,
        cantidadDevuelta: 1,
        fechaDevolucion: this.getLocalDateString(),
        condicion: "bueno",
        observaciones: "",
        idUsuarioRecibe: ""
      }
    };
  },
  async mounted() {
    await this.fetchSalidas();
    await this.fetchUsuarios();
    
    // Check for ID in route params
    const routeSalidaId = this.$route.params.idSalida;
    if (routeSalidaId) {
      this.form.idSalida = parseInt(routeSalidaId);
      this.onSalidaChange();
    }

    // Set default receiving user
    const currentUser = getCurrentUser();
    if (currentUser) {
      this.form.idUsuarioRecibe = currentUser.idUsuario;
    }
  },
  methods: {
    getLocalDateString() {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      return now.toISOString().slice(0, 16);
    },
    async fetchSalidas() {
      try {
        const res = await obtenerSalidasHerramienta();
        if (res.success) {
          // Only show pending ones
          this.salidasPendientes = (res.data || []).filter(s => 
            s.estado !== 'Devuelto' && s.estado !== 'devuelto'
          );
        }
      } catch (error) {
        console.error("Error fetching salidas:", error);
      }
    },
    async fetchUsuarios() {
      try {
        const res = await obtenerUsuarios();
        if (res.success) {
          this.usuarios = res.data || [];
        }
      } catch (error) {
        console.error("Error fetching usuarios:", error);
      }
    },
    onSalidaChange() {
      this.selectedSalida = this.salidasPendientes.find(s => s.idSalida === this.form.idSalida);
      if (this.selectedSalida) {
        this.form.cantidadDevuelta = this.selectedSalida.cantidadSalida;
        this.maxCantidad = this.selectedSalida.cantidadSalida;
      }
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    async handleSubmit() {
      if (!this.form.idSalida || !this.form.idUsuarioRecibe) {
        this.toast.warning("Por favor complete los campos obligatorios");
        return;
      }

      if (this.form.cantidadDevuelta > this.maxCantidad) {
        this.toast.warning(`La cantidad no puede superar los ${this.maxCantidad} prestados.`);
        return;
      }

      this.loadingSubmit = true;
      try {
        const res = await crearDevolucionHerramienta(this.form);
        if (res.success) {
          this.toast.success("Devolución registrada correctamente");
          this.$router.push('/salida-herramienta');
        } else {
          this.toast.error("Error: " + res.error);
        }
      } catch (error) {
        this.toast.error("Error de conexión");
      } finally {
        this.loadingSubmit = false;
      }
    }
  }
};
</script>

<style scoped>
select {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
}
</style>
